/**
 * Maps a server/network error to a displayable result for PRE-AUTH screens.
 *
 * WHY hardened: a Codex adversarial review flagged that showing the raw error
 * `message` or internal/5xx detail to an UNAUTHENTICATED user leaks backend/
 * provider internals and widens the attack surface on rare failure paths.
 * Rule: operational 4xx with a clean FR backendMessage → show it; everything
 * internal/unknown/network → fixed generic FR + requestId only (raw goes to
 * server-side telemetry, never the DOM).
 */
export interface ResolvedAuthError {
  // WHY `string` not `'email'`: the literal was tightened during the CONFLICT-only
  // era when AUTH_ERROR_MAP only targeted the email field. The spec §4.3 details.issues
  // re-map can target any field (password, fullName, cabinetName…). Callers that feed
  // this into RHF form.setError() should cast: `fieldError.name as Parameters<typeof
  // form.setError>[0]` — the cast is documented at every call site.
  fieldError?: { name: string; message: string };
  toastMessage?: string;
}

/** Minimal ZodIssue shape — DO NOT import zod here to avoid a circular dep risk. */
interface ZodIssueLike {
  path: Array<string | number>;
  message: string;
}

interface ErrMeta {
  errorCode?: string;
  backendMessage?: string;
  requestId?: string;
  httpStatus?: number;
  /** Backend serializes Zod validation errors as details.issues (ZodIssue[]). */
  details?: { issues?: ZodIssueLike[] };
}

// Minimal map: only codes that deserve a rewritten message / CTA / field target.
// Backend userMessages that are already clean FR are NOT duplicated here.
const AUTH_ERROR_MAP: Record<string, { message: string; target: 'field:email' | 'toast' }> = {
  CONFLICT: {
    message: 'Cet email est déjà utilisé. Connectez-vous ou utilisez une autre adresse.',
    target: 'field:email',
  },
};

const GENERIC = 'Une erreur technique est survenue. Réessayez dans un instant.';

function isErrMeta(v: unknown): v is ErrMeta {
  return typeof v === 'object' && v !== null;
}

/** Internal/unknown/network: never show raw, only generic (+requestId). */
function isInternal(m: ErrMeta): boolean {
  return m.errorCode === 'INTERNAL_ERROR' || (m.httpStatus ?? 0) >= 500 || !m.errorCode;
}

/**
 * Resolves a server or network error to a safe, user-facing message for pre-auth screens.
 *
 * WHY: pre-auth = unauthenticated trust boundary. Raw error strings (JS `message`,
 * backend stack traces, pool names) must never reach the DOM. Only clean operational
 * 4xx backendMessages are forwarded; everything else collapses to the generic FR string.
 */
export function resolveAuthError(err: unknown): ResolvedAuthError {
  const m: ErrMeta = isErrMeta(err) ? (err as ErrMeta) : {};

  // Object.hasOwn guard: prototype-name errorCodes ('__proto__', 'toString',
  // 'constructor') are truthy via Object.prototype and would resolve to a
  // phantom mapping → silent { toastMessage: undefined }. hasOwn restricts the
  // lookup to keys actually declared in AUTH_ERROR_MAP.
  const mapped =
    m.errorCode && Object.hasOwn(AUTH_ERROR_MAP, m.errorCode)
      ? AUTH_ERROR_MAP[m.errorCode]
      : undefined;
  if (mapped?.target === 'field:email') {
    return { fieldError: { name: 'email', message: mapped.message } };
  }
  if (mapped) {
    return { toastMessage: mapped.message };
  }

  // Internal / unknown / network → generic + requestId, NEVER the raw message
  // (anti-leak: pre-auth trust boundary). This branch MUST stay before the
  // details.issues branch: an INTERNAL_ERROR that happens to carry details
  // must still collapse to generic — details are never read for internal errors.
  if (isInternal(m)) {
    return {
      toastMessage: m.requestId ? `${GENERIC} Référence : ${m.requestId}` : GENERIC,
    };
  }

  // spec §4.3: backend Zod validation error — re-map the first issue to a field.
  // WHY both fieldError AND toastMessage: fieldError lets a form show it inline;
  // toastMessage is the anti-"opaque generic" guarantee when the form has no field
  // matching `name` (e.g. backend sends `fullName` but the form splits it into
  // firstName/lastName — RHF no-ops the setError, but the precise FR message still
  // reaches the user via the toast). The issue.message is already FR-localized
  // (Task 1 localized the shared Zod schemas).
  const issues = m.details?.issues;
  if (Array.isArray(issues) && issues.length > 0) {
    // WHY explicit undefined check after length > 0: strict TS (noUncheckedIndexedAccess)
    // types array[0] as T | undefined even after a length guard — the narrowing only
    // happens with a destructuring or an explicit undefined check.
    const issue: ZodIssueLike | undefined = issues[0];
    if (issue !== undefined) {
      const name = String(issue.path[0] ?? '');
      const message = issue.message;
      return {
        fieldError: name ? { name, message } : undefined,
        toastMessage: message,
      };
    }
  }

  // Operational non-mapped (4xx): backend FR userMessage is already localized
  // and safe to display.
  if (m.backendMessage) {
    return { toastMessage: m.backendMessage };
  }

  return { toastMessage: GENERIC };
}
